import { Entity } from "../../engine/entity/Entity";

import { Breed } from "../../engine/entity/Breed";
import { ActionController } from "./controllers/genericController";
import { Item } from "../items/Item";
import { SparseMap } from "../../lib/sparseMap";
import { Particle } from "../../engine/anims/Particle";
import { makeDefaultAIProps } from "./properties/aiProperties";
import {
  BreedAttributeProperties,
  makeBaseStats,
} from "./properties/attributeProperties";
import { makeStatus, StatusProperties } from "./properties/statusProperties";
import { Stat } from "./Stat";
import triggers from "./triggers/triggers";
import { Point } from "../../engine/space/bounds";
import { Spell } from "./Spell";
import { SpellTypes } from "../../data/spells";

export class ActorBreed extends Breed {
  baseStats: BreedAttributeProperties;
  triggers: Record<string, [string, any][]>;
  defaultWielding: [string, number][];
  defaultContains: [string, number][];
  defaultSpellBook: any[] | null;
  controller: ActionController | null;
  aiTraits: string[] | null;
  pieceBreed: Breed | null;
  initialPieces: string | null;
  isCapital = false;
  aiWords: string[] | undefined;

  constructor(key: string, breedData: any) {
    super(key, breedData);

    // Stat and Triggers
    this.baseStats = breedData.baseStats || makeBaseStats();
    this.triggers = breedData.triggers || {};

    // Item Properties
    this.defaultWielding = breedData.wielding || [];
    this.defaultContains = breedData.contains || [];
    this.defaultSpellBook = breedData.spellBook || [];

    // Letter Properties
    this.pieceBreed = new Breed("PIECE_TILE", breedData);
    this.initialPieces = breedData.initialPieces;
    this.isCapital = breedData.isCapital;

    // AI Properties
    this.controller = breedData.actionController;
    this.aiTraits = breedData.aiTraits;
    this.aiWords = breedData.aiWords;
  }
}

export class Actor extends Entity<ActorBreed> {
  alive = true;

  // Stats & Attributes
  level: number;
  health: Stat;
  hunger: Stat;
  maxLetters: number;

  status: StatusProperties = makeStatus();

  // Letter Data
  pieceDirection: Point | undefined;
  spellWand: string[] = [];

  events = {
    playedWord: [],
    endOfTurn: [],
  } as Record<string, [string, any][]>;

  // Child Entities
  pieces: SparseMap<number, Piece> = new SparseMap();
  ownedParticles: Map<number, Particle<any>> = new Map();

  wielding: SparseMap<number, Item> = new SparseMap();
  contains: SparseMap<number, Item> = new SparseMap();
  book: SparseMap<string, Spell> = new SparseMap();

  // AI Properties
  controllerProps: any;
  aiProps = makeDefaultAIProps();

  constructor(id: number, breed: ActorBreed, x: number, y: number) {
    super(id, breed, x, y);

    breed.defaultSpellBook?.forEach((spell) => {
      if (typeof spell === "string") {
        const info = new Spell(SpellTypes[spell]);
        this.book.add(info.id, info);
      } else {
        this.book.add(spell.id, new Spell(spell));
      }
    });

    if (this.breed.aiWords) {
      this.aiProps.words?.push(...this.breed.aiWords);
    }

    // Stat Initialization
    this.level = breed.baseStats.level;
    this.maxLetters = breed.baseStats.maxLetters || 0;

    this.health = new Stat(
      breed.baseStats.health,
      this.onEvent.bind(this, "health"),
      true
    );

    this.hunger = new Stat(
      breed.baseStats.hunger,
      this.onEvent.bind(this, "hunger"),
      true
    );

    // Event Setup
    this.events = breed.triggers;

    // AI Initialization
    this.aiProps.spawnLocation = { x, y };
  }

  plan() {
    if (this.alive) {
      return this.breed.controller && this.breed.controller(this);
    }
  }

  end() {
    this.alive = false;
    this.ownedParticles.forEach((p) => p.finish());
    this.pieces.forEach((p) => p.end());
  }

  onEvent(event: string, params: any) {
    const handlers = this.events[event];

    handlers?.forEach((trigger) => {
      triggers[trigger[0]](this, trigger[1], params);
    });
  }

  async addParticle(p: Particle<any>) {
    this.ownedParticles.set(p.id, p);
    await p.status;
    this.ownedParticles.delete(p.id);
  }
}

export class Piece extends Entity<Breed> {
  ch: string | null;
  owner = 0;

  constructor(id: number, breed: Breed, x: number, y: number, char = "") {
    super(id, breed, x, y);
    this.ch = char || breed.ch;
  }

  end() {
    //this.owner = 0;
  }
}
